export const SURVEY_VERSION = 4;
export const MAX_SURVEY_CHOICES = 2;
export const SURVEY_OPTIONS = [
  ["shopping", "Lista de compras pronta para a semana"],
  ["meal_prep", "Ajuda para organizar o preparo das refeições da semana"],
  ["take_a_break", "Tirar alguns dias de pausa, sem perder meu progresso no app nem receber lembretes"],
  ["budget_menu", "Um cardápio que caiba melhor no meu bolso"],
  ["other", "Outra melhoria"],
  ["none", "Não sinto falta de nada agora"],
] as const;
export type SurveyChoice = (typeof SURVEY_OPTIONS)[number][0];
export type SurveyResponse = { choices: SurveyChoice[]; other_text: string | null };
const API = import.meta.env.VITE_API_URL || "https://api.ondiet.app";

export function validateSurvey(choices: SurveyChoice[], otherText: string): string | null {
  if (choices.length === 0) return "Selecione pelo menos uma opção.";
  if (choices.length > MAX_SURVEY_CHOICES) return "Marque até 2 opções.";
  if (new Set(choices).size !== choices.length) return "Opções repetidas.";
  if (choices.includes("none") && choices.length !== 1) return "Não sinto falta de nada agora é exclusiva.";
  if (choices.includes("other") && !otherText.trim()) return "Escreva sua outra ideia.";
  if (otherText.length > 280) return "Use até 280 caracteres.";
  return null;
}

export function toggleSurveyChoice(choices: SurveyChoice[], choice: SurveyChoice): SurveyChoice[] {
  if (choices.includes(choice)) return choices.filter(value => value !== choice);
  if (choice === "none") return ["none"];
  const selected = choices.filter(value => value !== "none");
  return selected.length < MAX_SURVEY_CHOICES ? [...selected, choice] : selected;
}

export function shouldRequestSurvey({ ready, visible, busy, dialogOpen }: {
  ready: boolean; visible: boolean; busy: boolean; dialogOpen: boolean;
}): boolean { return ready && visible && !busy && !dialogOpen; }

export function shouldStartNewSurveyLaunch(storedTimeOrigin: number, navigationType: string, currentTimeOrigin: number): boolean {
  return navigationType === "navigate" && storedTimeOrigin !== currentTimeOrigin;
}

export function getSurveyLaunchId(): string | null {
  try {
    const key = `ondiet-product-survey-launch-v${SURVEY_VERSION}`;
    const stored = sessionStorage.getItem(key);
    const navigationType = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type || "navigate";
    const currentTimeOrigin = performance.timeOrigin;
    let previous: { id: string; timeOrigin: number } | null = null;
    if (stored) {
      try { previous = JSON.parse(stored) as { id: string; timeOrigin: number }; }
      catch { previous = { id: stored, timeOrigin: currentTimeOrigin }; }
    }
    const id = previous?.id && !shouldStartNewSurveyLaunch(previous.timeOrigin, navigationType, currentTimeOrigin)
      ? previous.id : crypto.randomUUID();
    sessionStorage.setItem(key, JSON.stringify({ id, timeOrigin: currentTimeOrigin }));
    return id;
  } catch { return null; }
}

async function surveyFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API}/api/profile/product-survey${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...init?.headers },
  });
  if (!response.ok) throw new Error(`Pesquisa indisponível (${response.status})`);
  const data = await response.json();
  if (data.success !== true) throw new Error("Resposta inválida da pesquisa");
  return data as T;
}

export const productSurveyApi = {
  opening: (token: string, launchId: string, ready: boolean) => surveyFetch<{ success: true; show: boolean }>("/opening", token, { method: "POST", body: JSON.stringify({ version: SURVEY_VERSION, launch_id: launchId, ready }) }),
  shown: (token: string, launchId: string) => surveyFetch<{ success: true; shown: boolean }>("/shown", token, { method: "POST", body: JSON.stringify({ version: SURVEY_VERSION, launch_id: launchId }) }),
  dismiss: (token: string, launchId: string) => surveyFetch<{ success: true; dismissed: boolean }>("/dismiss", token, { method: "POST", body: JSON.stringify({ version: SURVEY_VERSION, launch_id: launchId }) }),
  save: (token: string, response: SurveyResponse) => surveyFetch<{ success: true; response: SurveyResponse }>("/response", token, { method: "PUT", body: JSON.stringify({ version: SURVEY_VERSION, ...response }) }),
};
